import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { Link } from 'react-router-dom';
import { NEARBY_CATEGORY_ICONS } from './project-steps/constants';

const AdminScoreManagement: React.FC = () => {
    const { token, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    const [settings, setSettings] = useState<any>({
        showTrustScore: true,
        amenitiesMaxScore: '50+',
        constructionStatusOptions: [],
        legalTrustRules: [],
        localityScoreRules: []
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const data = await api.getScoreSettings();
            if (data && !data.message) {
                setSettings(data);
            }
        } catch (error) {
            console.error('Error fetching score settings:', error);
            toast.error('Failed to load score settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!token) return;
        setSaving(true);
        try {
            const data = await api.updateScoreSettings(token, settings);
            if (data && !data.message) {
                toast.success('Score settings updated successfully');
                setSettings(data);
            } else {
                toast.error(data.message || 'Failed to update settings');
            }
        } catch (error) {
            console.error('Error updating score settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    // Handlers for Construction Status Options
    const addConstructionOption = () => {
        setSettings({
            ...settings,
            constructionStatusOptions: [
                ...settings.constructionStatusOptions,
                { label: 'New Status', meaning: 'New Meaning', score: 5 }
            ]
        });
    };

    const updateConstructionOption = (index: number, field: string, value: any) => {
        const newOptions = [...settings.constructionStatusOptions];
        newOptions[index] = { ...newOptions[index], [field]: value };
        setSettings({ ...settings, constructionStatusOptions: newOptions });
    };

    const removeConstructionOption = (index: number) => {
        const newOptions = settings.constructionStatusOptions.filter((_: any, i: number) => i !== index);
        setSettings({ ...settings, constructionStatusOptions: newOptions });
    };

    // Handlers for Legal Trust Rules
    const updateLegalRule = (index: number, field: string, value: any) => {
        const newRules = [...settings.legalTrustRules];
        newRules[index] = { ...newRules[index], [field]: value };
        setSettings({ ...settings, legalTrustRules: newRules });
    };

    // Handlers for Locality Score Rules
    const addLocalityRule = () => {
        setSettings({
            ...settings,
            localityScoreRules: [
                ...(settings.localityScoreRules || []),
                { category: Object.keys(NEARBY_CATEGORY_ICONS)[0], maxDistanceKm: 2, points: 5 }
            ]
        });
    };

    const updateLocalityRule = (index: number, field: string, value: any) => {
        const newRules = [...(settings.localityScoreRules || [])];
        newRules[index] = { ...newRules[index], [field]: value };
        setSettings({ ...settings, localityScoreRules: newRules });
    };

    const removeLocalityRule = (index: number) => {
        const newRules = (settings.localityScoreRules || []).filter((_: any, i: number) => i !== index);
        setSettings({ ...settings, localityScoreRules: newRules });
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            </AdminLayout>
        );
    }

    if (user?.role === 'sub-admin' && !user.permissions?.includes('Manage Packages') && !user.permissions?.includes('Admin Access')) {
        // Just a basic check if they shouldn't access this (or add a specific permission later)
    }

    return (
        <AdminLayout>
            <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
                <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div>
                        <h1 className="text-2xl font-black text-gray-800">Score Management System</h1>
                        <p className="text-sm text-gray-500 font-medium mt-1">Configure how dynamic scores and trust indicators are calculated for projects.</p>
                    </div>
                    <button 
                        onClick={handleSave} 
                        disabled={saving}
                        className="px-6 py-2.5 bg-indigo-600 text-white font-bold rounded-xl shadow-lg shadow-indigo-200 hover:bg-indigo-700 hover:-translate-y-0.5 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-save"></i>}
                        Save Changes
                    </button>
                </div>

                {/* General Settings */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                            <i className="fa-solid fa-gear"></i>
                        </div>
                        <h2 className="text-lg font-bold text-gray-800">General Display Settings</h2>
                    </div>
                    <div className="p-6">
                        <div className="flex items-center justify-between p-4 rounded-xl border transition-colors bg-gray-50 border-gray-200">
                            <div className="flex items-center gap-3">
                                <button 
                                    onClick={() => setSettings({...settings, showTrustScore: settings.showTrustScore !== false ? false : true})}
                                    className={`w-12 h-6 rounded-full relative transition-colors ${settings.showTrustScore !== false ? 'bg-emerald-500' : 'bg-gray-300'}`}
                                >
                                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${settings.showTrustScore !== false ? 'left-7' : 'left-1'}`}></div>
                                </button>
                                <div>
                                    <div className="font-bold text-gray-800 flex items-center gap-2">
                                        <i className="fa-solid fa-eye text-gray-400"></i> Show Trust Score
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">Toggle visibility of the Trust Score section on the project overview page.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Amenities Score */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                <i className="fa-solid fa-wand-magic-sparkles"></i>
                            </div>
                            <h2 className="text-lg font-bold text-gray-800">Lifestyle & Value Score (Amenities)</h2>
                        </div>
                        <Link to="/admin-amenities" className="text-sm font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-2 bg-white px-4 py-1.5 rounded-lg shadow-sm border border-gray-200">
                            <i className="fa-solid fa-up-right-from-square"></i> Edit Amenity Points
                        </Link>
                    </div>
                    <div className="p-6">
                        <p className="text-sm text-gray-500 mb-4 font-medium">This score adds up the individual point values of all selected amenities. You can configure the "Max Score" text displayed on the UI.</p>
                        <div className="max-w-xs">
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Display Max Score (e.g. 50+)</label>
                            <input 
                                type="text" 
                                value={settings.amenitiesMaxScore || ''}
                                onChange={(e) => setSettings({...settings, amenitiesMaxScore: e.target.value})}
                                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 font-bold text-gray-800"
                            />
                        </div>
                    </div>
                </div>

                {/* Construction Status Options */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                            <i className="fa-solid fa-helmet-safety"></i>
                        </div>
                        <h2 className="text-lg font-bold text-gray-800">Construction Status Timeline Scores</h2>
                    </div>
                    <div className="p-6">
                        <p className="text-sm text-gray-500 mb-6 font-medium">Configure the options available in the "Timeline Status" dropdown and assign a score (usually 1-10) to each. This score dictates the "Construction Progress" completion bar.</p>
                        
                        <div className="space-y-4">
                            {settings.constructionStatusOptions?.map((opt: any, index: number) => (
                                <div key={index} className="flex flex-wrap md:flex-nowrap items-center gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200 relative group">
                                    <div className="flex-1 min-w-[200px]">
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Status Label</label>
                                        <input 
                                            type="text" 
                                            value={opt.label}
                                            onChange={(e) => updateConstructionOption(index, 'label', e.target.value)}
                                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 font-semibold text-gray-800"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-[200px]">
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Meaning (Sub-label)</label>
                                        <input 
                                            type="text" 
                                            value={opt.meaning}
                                            onChange={(e) => updateConstructionOption(index, 'meaning', e.target.value)}
                                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 text-sm text-gray-600"
                                        />
                                    </div>
                                    <div className="w-24">
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Score (1-10)</label>
                                        <input 
                                            type="number" 
                                            value={opt.score}
                                            onChange={(e) => updateConstructionOption(index, 'score', parseFloat(e.target.value))}
                                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 font-bold text-center"
                                        />
                                    </div>
                                    <button 
                                        onClick={() => removeConstructionOption(index)}
                                        className="w-10 h-10 mt-5 rounded-lg bg-rose-100 text-rose-600 hover:bg-rose-500 hover:text-white transition-colors flex items-center justify-center shrink-0"
                                        title="Remove Option"
                                    >
                                        <i className="fa-solid fa-trash"></i>
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button 
                            onClick={addConstructionOption}
                            className="mt-4 px-4 py-2 border-2 border-dashed border-amber-300 text-amber-600 hover:bg-amber-50 rounded-xl font-bold text-sm transition-colors flex items-center gap-2"
                        >
                            <i className="fa-solid fa-plus"></i> Add Status Option
                        </button>
                    </div>
                </div>

                {/* Legal Trust Score Rules */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                            <i className="fa-solid fa-scale-balanced"></i>
                        </div>
                        <h2 className="text-lg font-bold text-gray-800">Legal Trust Score Rules</h2>
                    </div>
                    <div className="p-6">
                        <p className="text-sm text-gray-500 mb-6 font-medium">Assign point values to the 4 key legal criteria. If a project meets the criteria, it will earn these points. The UI will sum these up for the total Legal Trust Score.</p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {settings.legalTrustRules?.map((rule: any, index: number) => (
                                <div key={index} className={`flex items-center justify-between p-4 rounded-xl border ${rule.enabled ? 'bg-emerald-50/30 border-emerald-200' : 'bg-gray-50 border-gray-200'} transition-colors`}>
                                    <div className="flex items-center gap-3">
                                        <button 
                                            onClick={() => updateLegalRule(index, 'enabled', !rule.enabled)}
                                            className={`w-12 h-6 rounded-full relative transition-colors ${rule.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
                                        >
                                            <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${rule.enabled ? 'left-7' : 'left-1'}`}></div>
                                        </button>
                                        <div>
                                            <div className="font-bold text-gray-800 flex items-center gap-2">
                                                <i className={`fa-solid ${rule.icon} text-gray-400`}></i> {rule.label}
                                            </div>
                                            <div className="text-[10px] text-gray-400 font-mono uppercase tracking-wider">{rule.key}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input 
                                            type="number"
                                            value={rule.points}
                                            disabled={!rule.enabled}
                                            onChange={(e) => updateLegalRule(index, 'points', parseInt(e.target.value))}
                                            className="w-16 px-2 py-1 text-center bg-white border border-gray-200 rounded-lg font-black focus:outline-none focus:border-emerald-500 disabled:opacity-50"
                                        />
                                        <span className="text-xs font-bold text-gray-400">PTS</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Section 4: Locality Trust Score Rules */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                <i className="fa-solid fa-map-location-dot"></i>
                            </div>
                            <h2 className="text-lg font-bold text-gray-800">Locality Trust Score Rules</h2>
                        </div>
                        <button
                            onClick={addLocalityRule}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition shadow-sm"
                        >
                            <i className="fa-solid fa-plus mr-2"></i> Add Rule
                        </button>
                    </div>
                    <div className="p-6">
                        <p className="text-sm text-gray-500 mb-6 font-medium">Assign point values for having nearby landmarks within a specific distance (e.g. 5 points if Hospital is within 2km). A category will only award points once.</p>

                        <div className="space-y-4">
                            {settings.localityScoreRules?.map((rule: any, index: number) => (
                                <div key={index} className="grid grid-cols-12 gap-4 items-center bg-gray-50 p-4 rounded-xl border border-gray-200 relative group transition-colors hover:border-blue-300">
                                    
                                    <div className="col-span-12 md:col-span-4">
                                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Landmark Category</label>
                                        <select
                                            value={rule.category}
                                            onChange={(e) => updateLocalityRule(index, 'category', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm font-bold bg-white text-gray-700"
                                        >
                                            {Object.keys(NEARBY_CATEGORY_ICONS).map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-span-12 md:col-span-4">
                                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Within Distance (KM)</label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                step="0.1"
                                                min="0.1"
                                                value={rule.maxDistanceKm}
                                                onChange={(e) => updateLocalityRule(index, 'maxDistanceKm', parseFloat(e.target.value) || 0)}
                                                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm font-bold bg-white text-gray-700"
                                            />
                                            <span className="absolute right-3 top-2.5 text-gray-400 text-xs font-bold">KM</span>
                                        </div>
                                    </div>

                                    <div className="col-span-12 md:col-span-3">
                                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Points</label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                min="1"
                                                value={rule.points}
                                                onChange={(e) => updateLocalityRule(index, 'points', parseInt(e.target.value) || 0)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm font-black text-center bg-white text-gray-700"
                                            />
                                            <span className="absolute right-3 top-2.5 text-gray-400 text-xs font-bold">PTS</span>
                                        </div>
                                    </div>

                                    <div className="col-span-12 md:col-span-1 flex justify-end md:mt-5">
                                        <button
                                            onClick={() => removeLocalityRule(index)}
                                            className="w-10 h-10 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-colors border border-transparent hover:border-red-600"
                                            title="Remove Rule"
                                        >
                                            <i className="fa-solid fa-trash-can"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {(!settings.localityScoreRules || settings.localityScoreRules.length === 0) && (
                                <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-gray-400 mx-auto mb-3 shadow-sm">
                                        <i className="fa-solid fa-map-location-dot text-2xl"></i>
                                    </div>
                                    <h4 className="text-gray-600 font-bold">No rules configured</h4>
                                    <p className="text-sm text-gray-400 mt-1">Add rules to automatically calculate the Locality Trust Score.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminScoreManagement;
